import type { TFunction } from 'i18next';
import type { DeviceStatus, DeviceType } from '@/modules/device/lib/device.schema';
import { formatDateFull } from '@/shared/lib/format';
import { DEVICE_STATUS_COLOR, DEVICE_TYPES } from './device.constants';

type DeviceT = TFunction<'device'>;

export const formatDeviceDate = (date: string | null | undefined, t: DeviceT): string => {
  if (!date) {
    return t('lastSeen.notProvided');
  }
  return formatDateFull(date);
};

const ONLINE_WINDOW = 30 * 1000;

export const isOnline = (lastSeenAt: string | null): boolean => {
  if (!lastSeenAt) {
    return false;
  }
  return Date.now() - new Date(lastSeenAt).getTime() < ONLINE_WINDOW;
};

export const deviceStatusColor = (status: DeviceStatus) => DEVICE_STATUS_COLOR[status];

export const formatDuration = (ms: number): string => {
  if (!Number.isFinite(ms) || ms < 0) {
    return '—';
  }
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) {
    return `${totalSeconds}s`;
  }
  const minutes = Math.floor(totalSeconds / 60);
  if (minutes < 60) {
    return `${minutes}m ${totalSeconds % 60}s`;
  }
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
};

export const formatLastSeen = (date: string | null, t: DeviceT): string => {
  if (!date) {
    return t('lastSeen.never');
  }
  const now = Date.now();
  const then = new Date(date).getTime();
  const diffMs = now - then;

  if (diffMs < 60_000) {
    return t('lastSeen.justNow');
  }
  if (diffMs < 3_600_000) {
    return t('lastSeen.minutesAgo', { count: Math.floor(diffMs / 60_000) });
  }
  if (diffMs < 86_400_000) {
    return t('lastSeen.hoursAgo', { count: Math.floor(diffMs / 3_600_000) });
  }
  if (diffMs < 2_592_000_000) {
    return t('lastSeen.daysAgo', { count: Math.floor(diffMs / 86_400_000) });
  }
  return formatDeviceDate(date, t);
};

export const deviceTypeLabel = (type: DeviceType, t: DeviceT): string => {
  const entry = DEVICE_TYPES.find((d) => d.value === type);
  return entry ? t(entry.labelKey) : type;
};
